import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { HttpClientModule } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef } from '@angular/material/dialog';
import { DataService, SubjectZeroResponse, TopicTreeNode, MultipleTopicTreesResponse } from '../../services/data.service';
import { FileService } from '../../services/file.service';
import { InternalApiService, SubjectSection, ApiSubject } from '../../services/internal-api.service';

interface Subject {
  subject_id: string;
  subject_name: string;
}

interface Section {
  section_id: string;
  section_name: string;
}

@Component({
  selector: 'app-bundle-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatProgressBarModule,
    HttpClientModule,
    MatCardModule,
    MatIconModule
  ],
  templateUrl: './bundle-upload.component.html',
  styleUrls: ['./bundle-upload.component.css']
})
export class BundleUploadComponent implements OnInit {
  subjects: Subject[] = [];
  sections: Section[] = [];
  selectedSubject: string | null = null;
  selectedSection: string | null = null;
  selectedSubjectId: string | null = null;
  topic: string = '';
  subtopic: string = '';
  
  subjectTrees: MultipleTopicTreesResponse | null = null;
  
  // For file uploads
  fileTypes = ['txt_file', 'docx_file', 'pdf_file', 'xlsx_file', 'png_file', 'html_file', 'png_thumbnail_file', 'gif_file'];
  files: { [key: string]: File | null } = {};
  fileUploading: { [key: string]: boolean } = {};
  fileSuccess: { [key: string]: boolean } = {};
  fileError: { [key: string]: string | null } = {};
  uploadProgress: number = 0;
  isSubmitting: boolean = false;
  uploadSuccess: boolean = false;
  uploadError: string | null = null;

  constructor(
    private dataService: DataService,
    private fileService: FileService,
    private internalApiService: InternalApiService,
    private dialogRef: MatDialogRef<BundleUploadComponent>
  ) {
    // Initialize files object with all the file types
    this.fileTypes.forEach(type => {
      this.files[type] = null;
      this.fileUploading[type] = false;
      this.fileSuccess[type] = false;
      this.fileError[type] = null;
    });
  }

  ngOnInit(): void {
    this.loadSubjects();
  }

  loadSubjects(): void {
    // Try to use the internal API first
    console.log('Fetching subjects from internal API...');
    this.internalApiService.getSubjectsSections().subscribe({
      next: (response: SubjectSection) => {
        console.log('Raw API response:', response);
        if (response && response.subjects && response.subjects.length > 0) {
          // Map the API response to our interface
          this.subjects = response.subjects.map(subject => ({
            subject_id: subject.id,
            subject_name: subject.name_eng
          }));
          console.log('Successfully mapped subjects:', this.subjects);
          
          // Load all subject trees after getting subjects
          this.loadAllSubjectTrees();
        } else {
          console.warn('No subjects found in API response, falling back to data service');
          // Fall back to the regular API if needed
          this.loadSubjectsFromDataService();
        }
      },
      error: (error) => {
        console.error('Error loading subjects from internal API:', error);
        // Fall back to the regular API
        this.loadSubjectsFromDataService();
      }
    });
  }

  loadAllSubjectTrees(): void {
    // Extract all subject IDs
    const topicIds = this.subjects.map(subject => parseInt(subject.subject_id));
    
    // Call the new API endpoint that fetches all trees at once
    this.dataService.getAllTopicTrees(topicIds).subscribe({
      next: (response: MultipleTopicTreesResponse) => {
        console.log('All subject trees response:', response);
        this.subjectTrees = response;
        
        // If a subject is already selected, try to load its sections from the newly loaded trees
        if (this.selectedSubjectId && this.subjectTrees.trees[this.selectedSubjectId]) {
          console.log(`Tree data loaded, now loading sections for already selected subject: ${this.selectedSubjectId}`);
          this.loadSectionsFromCache(this.selectedSubjectId);
        }
      },
      error: (error: any) => {
        console.error('Error loading all subject trees:', error);
        
        // If a subject is selected but we couldn't load the trees, try to load sections from internal API
        if (this.selectedSubjectId && this.selectedSubject) {
          console.log(`Failed to load trees, falling back to internal API for sections of subject: ${this.selectedSubject}`);
          this.loadSections(this.selectedSubject);
        }
      }
    });
  }

  loadSubjectsFromDataService(): void {
    this.dataService.getSubjectsFromZeroLevel().subscribe({
      next: (response: SubjectZeroResponse) => {
        if (response && response.topics && response.topics.length > 0) {
          this.subjects = response.topics.map(topic => ({
            subject_id: topic.topic_id,
            subject_name: topic.name_eng
          }));
          
          // Load all subject trees after getting subjects
          this.loadAllSubjectTrees();
        }
      },
      error: (error) => {
        console.error('Error loading subjects from data service:', error);
      }
    });
  }

  onSubjectChange(): void {
    if (this.selectedSubject) {
      // Find the selected subject ID
      const subject = this.subjects.find(s => s.subject_name === this.selectedSubject);
      if (subject) {
        this.selectedSubjectId = subject.subject_id;
        // Get sections from the cached tree data if available
        if (this.subjectTrees && this.subjectTrees.trees[this.selectedSubjectId]) {
          console.log(`Using cached tree data for subject ID: ${this.selectedSubjectId}`);
          this.loadSectionsFromCache(this.selectedSubjectId);
        } else {
          console.log(`No cached tree data for subject ID: ${this.selectedSubjectId}, need to load subjects trees first`);
          // If tree data isn't available, load all trees and then try again
          this.loadAllSubjectTrees();
        }
      }
    } else {
      this.sections = [];
      this.selectedSection = null;
    }
  }

  loadSectionsFromCache(subjectId: string): void {
    const treeData = this.subjectTrees?.trees[subjectId];
    if (treeData && treeData.children && treeData.children.length > 0) {
      this.sections = treeData.children.map(section => ({
        section_id: section.topic_id,
        section_name: section.name_eng
      }));
      console.log('Loaded sections from cached tree data:', this.sections);
    } else {
      this.sections = [];
      console.log('No sections found in cached tree data for subject ID:', subjectId);
    }
  }

  loadSections(subject: string): void {
    // Try to use the internal API
    this.internalApiService.getSectionsForSubject(subject).subscribe({
      next: (sectionNames) => {
        if (sectionNames && sectionNames.length > 0) {
          // Map string array to section objects
          this.sections = sectionNames.map(name => ({
            section_id: name, // Using the name as ID for this case
            section_name: name
          }));
          console.log('Loaded sections from internal API:', this.sections);
        }
      },
      error: (error) => {
        console.error('Error loading sections from internal API:', error);
        this.sections = [];
      }
    });
  }

  onFileSelected(event: Event, fileType: string): void {
    const element = event.target as HTMLInputElement;
    const files = element.files;
    
    console.log(`File selection for ${fileType}:`, files);
    
    if (files && files.length > 0) {
      this.files[fileType] = files[0];
      this.fileError[fileType] = null;
      console.log(`Selected file for ${fileType}:`, this.files[fileType]);
    }
  }

  triggerFileInput(fileType: string): void {
    const fileInput = document.getElementById(`file-${fileType}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  removeFile(fileType: string): void {
    this.files[fileType] = null;
    // Reset the file input
    const fileInput = document.getElementById(`file-${fileType}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  validateForm(): boolean {
    // Check if subject and section are selected
    if (!this.selectedSubject || !this.selectedSection) {
      this.uploadError = 'Please select both subject and section';
      return false;
    }

    // Check if at least one file is selected
    const hasFiles = Object.values(this.files).some(file => file !== null);
    if (!hasFiles) {
      this.uploadError = 'Please upload at least one file';
      return false;
    }

    return true;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  createBundle(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;
    this.uploadProgress = 0;
    this.uploadError = null;
    
    // Create FormData for the request
    const formData = new FormData();
    formData.append('subject', this.selectedSubject as string);
    formData.append('section', this.selectedSection as string);
    
    if (this.topic) {
      formData.append('topic', this.topic);
    }
    
    if (this.subtopic) {
      formData.append('subtopic', this.subtopic);
    }
    
    // Add files to the formData using the correct parameter names
    this.fileTypes.forEach(type => {
      if (this.files[type]) {
        formData.append(type, this.files[type] as File);
      }
    });
    
    console.log('Sending bundle creation request with form data:', 
      Array.from(formData.entries()).map(entry => `${entry[0]}: ${entry[1] instanceof File ? entry[1].name : entry[1]}`));
    
    // Call the API to create a new bundle
    this.fileService.createFileBundle(formData).subscribe({
      next: (response: any) => {
        console.log('Bundle creation response:', response);
        this.isSubmitting = false;
        this.uploadSuccess = true;
        this.uploadProgress = 100;
        
        // Reset the form and close dialog after success
        setTimeout(() => {
          this.resetForm();
          this.dialogRef.close(true); // Close with success result
        }, 1500);
      },
      error: (error: any) => {
        this.isSubmitting = false;
        this.uploadError = 'Failed to create bundle. Please try again.';
        console.error('Error creating bundle:', error);
      }
    });
  }

  resetForm(): void {
    this.selectedSubject = null;
    this.selectedSection = null;
    this.selectedSubjectId = null;
    this.topic = '';
    this.subtopic = '';
    this.uploadSuccess = false;
    this.uploadError = null;
    this.uploadProgress = 0;
    
    // Reset files
    this.fileTypes.forEach(type => {
      this.files[type] = null;
      this.fileUploading[type] = false;
      this.fileSuccess[type] = false;
      this.fileError[type] = null;
    });
    
    // Reset file inputs
    this.fileTypes.forEach(type => {
      const fileInput = document.getElementById(`file-${type}`) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    });
  }

  getFileAccept(fileType: string): string {
    switch (fileType) {
      case 'pdf_file':
        return '.pdf,application/pdf';
      case 'docx_file':
        return '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'txt_file':
        return '.txt,text/plain';
      case 'xlsx_file':
        return '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'png_file':
      case 'png_thumbnail_file':
        return '.png,image/png';
      case 'html_file':
        return '.html,text/html';
      case 'gif_file':
        return '.gif,image/gif';
      default:
        return '';
    }
  }

  // Add these methods to display user-friendly file type labels
  getFileLabel(fileType: string): string {
    switch (fileType) {
      case 'txt_file':
        return 'Text File';
      case 'docx_file':
        return 'Word Document';
      case 'pdf_file':
        return 'PDF Document';
      case 'xlsx_file':
        return 'Excel Spreadsheet';
      case 'png_file':
        return 'PNG Image';
      case 'html_file':
        return 'HTML File';
      case 'png_thumbnail_file':
        return 'Thumbnail Image';
      case 'gif_file':
        return 'GIF Image';
      default:
        return fileType.replace('_', ' ').toUpperCase();
    }
  }

  // Add method to show file extensions
  getFileExtension(fileType: string): string {
    switch (fileType) {
      case 'txt_file':
        return '.txt';
      case 'docx_file':
        return '.docx';
      case 'pdf_file':
        return '.pdf';
      case 'xlsx_file':
        return '.xlsx';
      case 'png_file':
      case 'png_thumbnail_file':
        return '.png';
      case 'html_file':
        return '.html';
      case 'gif_file':
        return '.gif';
      default:
        return '';
    }
  }
} 